export interface InitialOpenStateInput {
  /** open()/close() intent that arrived while init was in flight; null when none did. */
  explicitIntent: boolean | null;
  inline: boolean;
  autoOpen: boolean;
  fullscreen: boolean;
  /** The visitor closed this placement earlier in the browser session (LAS-4555). */
  dismissed: boolean;
  /** No toggle button renders, so a closed placement has no re-open affordance. */
  hideToggleButton: boolean;
}

export interface InitialOpenState {
  isOpen: boolean;
  isFullscreen: boolean;
}

/**
 * Decide whether a placement starts open, and whether auto-open applies its
 * fullscreen mode, once init has resolved.
 *
 * An explicit intent always wins. Inline placements start open by design and
 * never start fullscreen. Otherwise auto-open applies unless the visitor
 * dismissed the chat earlier in this session, so a close holds across page
 * loads instead of the chat reopening on every page. A dismissal only holds
 * where the toggle button renders: with the toggle hidden, a dismissed
 * placement would leave the visitor no way back to the chat.
 */
export const resolveInitialOpenState = ({
  explicitIntent,
  inline,
  autoOpen,
  fullscreen,
  dismissed,
  hideToggleButton,
}: InitialOpenStateInput): InitialOpenState => {
  if (explicitIntent !== null) {
    return { isOpen: explicitIntent, isFullscreen: false };
  }
  if (inline) {
    return { isOpen: true, isFullscreen: false };
  }
  const autoOpens = autoOpen && (hideToggleButton || !dismissed);
  return { isOpen: autoOpens, isFullscreen: autoOpens && fullscreen };
};
